import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logUserInteraction } from "~vue/utils/logUtils";

import type { WeeklyAction } from "./weeklyActions";

type PrimaryActionAnalyticsProperties = {
    development_action_channel: WeeklyAction["channel"];
    action_resource_type: NonNullable<WeeklyAction["resource"]>["type"];
};

type LinkWeeklyActionPrimaryAction = {
    kind: "link";
    ctaLabel: string;
    href: string;
    target: "_blank" | "_self";
    rel?: "noopener noreferrer";
    analyticsProperties: PrimaryActionAnalyticsProperties;
};

type StartConversationWeeklyActionPrimaryAction = {
    kind: "start_conversation";
    ctaLabel: string;
    initialUserMessage: string;
    analyticsProperties: PrimaryActionAnalyticsProperties;
};

export type WeeklyActionPrimaryAction = LinkWeeklyActionPrimaryAction | StartConversationWeeklyActionPrimaryAction;

type LearningMaterialWeeklyAction = Extract<WeeklyAction, { channel: "learning_material" }>;
type RolePlayWeeklyAction = Extract<WeeklyAction, { channel: "role_play_with_nadia" }>;
type ConversationWeeklyAction = Extract<WeeklyAction, { channel: "conversation_with_nadia" }>;
type ResourceFreeWeeklyAction = {
    channel: Exclude<WeeklyAction["channel"], "learning_material" | "role_play_with_nadia" | "conversation_with_nadia">;
    resource?: null;
};

const SAME_TAB_TELEMETRY_TIMEOUT_MS = 500;

function assertNever(value: never): never {
    throw new Error(`Unhandled Development Journeys action contract: ${JSON.stringify(value)}`);
}

function noPrimaryAction(action: ResourceFreeWeeklyAction): null {
    return action.resource ?? null;
}

function getLearningMaterialPrimaryAction(action: LearningMaterialWeeklyAction): WeeklyActionPrimaryAction | null {
    const resource = action.resource;
    if (resource === null) {
        return null;
    }

    switch (resource.type) {
        case "external_link":
            return {
                kind: "link",
                ctaLabel: resource.label,
                href: resource.url,
                target: "_blank",
                rel: "noopener noreferrer",
                analyticsProperties: {
                    development_action_channel: action.channel,
                    action_resource_type: resource.type,
                },
            };
        default:
            return assertNever(resource.type);
    }
}

function getRolePlayPrimaryAction(action: RolePlayWeeklyAction, rolePlaySetupUrl: string): LinkWeeklyActionPrimaryAction {
    return {
        kind: "link",
        ctaLabel: "Start role play",
        href: `${rolePlaySetupUrl}?persona=${encodeURIComponent(JSON.stringify(action.resource.role_play_context))}`,
        target: "_self",
        analyticsProperties: {
            development_action_channel: action.channel,
            action_resource_type: action.resource.type,
        },
    };
}

function getConversationPrimaryAction(action: ConversationWeeklyAction): StartConversationWeeklyActionPrimaryAction {
    return {
        kind: "start_conversation",
        ctaLabel: "Chat with Nadia",
        initialUserMessage: action.resource.initial_user_message,
        analyticsProperties: {
            development_action_channel: action.channel,
            action_resource_type: action.resource.type,
        },
    };
}

export function getWeeklyActionPrimaryAction(action: WeeklyAction, rolePlaySetupUrl: string): WeeklyActionPrimaryAction | null {
    switch (action.channel) {
        case "learning_material":
            return getLearningMaterialPrimaryAction(action);
        case "role_play_with_nadia":
            return getRolePlayPrimaryAction(action, rolePlaySetupUrl);
        case "conversation_with_nadia":
            return getConversationPrimaryAction(action);
        case "flow_of_work_meeting":
        case "flow_of_work_activity":
        case "action_review_reminder":
            return noPrimaryAction(action);
        default:
            return assertNever(action);
    }
}

export function logWeeklyActionPrimaryActionClicked(primaryAction: WeeklyActionPrimaryAction) {
    return logUserInteraction(AmplitudeEventNames.DEVELOPMENT_JOURNEYS_PRIMARY_ACTION_CLICKED, primaryAction.analyticsProperties);
}

async function waitForSameTabTelemetry(primaryAction: LinkWeeklyActionPrimaryAction): Promise<void> {
    let timeout: ReturnType<typeof globalThis.setTimeout> | undefined;
    try {
        await Promise.race([
            logWeeklyActionPrimaryActionClicked(primaryAction),
            new Promise<void>((resolve) => {
                timeout = globalThis.setTimeout(resolve, SAME_TAB_TELEMETRY_TIMEOUT_MS);
            }),
        ]);
    } finally {
        if (timeout !== undefined) {
            globalThis.clearTimeout(timeout);
        }
    }
}

export async function handleWeeklyActionPrimaryLinkClick(primaryAction: LinkWeeklyActionPrimaryAction, event: MouseEvent): Promise<void> {
    const isSameTabNavigation = primaryAction.target === "_self" && event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;

    if (!isSameTabNavigation) {
        void logWeeklyActionPrimaryActionClicked(primaryAction);
        return;
    }

    event.preventDefault();
    try {
        await waitForSameTabTelemetry(primaryAction);
    } finally {
        globalThis.location.href = primaryAction.href;
    }
}
